import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import React from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import Canvas from '@design-system/canvas';
import { Example } from '@component/iyagigo/common/explanation';
import { replaceDashWithTilde } from '@util/string';
import { v4 as uuid4 } from 'uuid';
import { useTypedSelector } from '@hook/common/useStore';

import { ETheme } from '@datatype/type';

// 어휘/문법 항목 공통 형태. ConversationVocabulary/Grammar, ExpressionVocabulary/Grammar 모두 호환.
interface IStudyNoteItem {
  keyword: string;
  meaning: string[];
  pronunciation: string[];
  usage: string;
  example: { sentence: string; translation: string }[];
  kanji?:
    | {
        character: string;
        meaning: string[];
        onyomi: string[];
        kunyomi: string[];
      }[]
    | null;
}

interface IProps {
  vocabularies?: IStudyNoteItem[] | null;
  grammars?: IStudyNoteItem[] | null;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  width: 100%;
`;

const MeaningContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: flex-end;
  gap: ${size.MARGIN_1};
`;

const KanjiYomiContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
`;

const StudyItemsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
`;

interface IStudyNoteItemProps {
  item: IStudyNoteItem;
  isJapanese: boolean;
}

const StudyNoteItem: React.FC<IStudyNoteItemProps> = ({ item, isJapanese }) => {
  const normalizedKeyword = replaceDashWithTilde(item.keyword);
  return (
    <Canvas theme={ETheme.GRAY} width='100%'>
      <ContentContainer>
        <MeaningContainer>
          <TextBlock
            text={normalizedKeyword}
            annotation={isJapanese && item.pronunciation?.[0] ? { reading: item.pronunciation[0] } : undefined}
            design={{
              text: { size: 'medium', color: color.BLACK_2, bold: true },
              box: { alignX: 'left', alignY: 'bottom' },
            }}
          />
          <TextBlock
            text={item.meaning.map((m) => replaceDashWithTilde(m)).join(', ')}
            design={{
              text: { size: 'medium', color: color.BLACK_2, bold: false },
              box: { alignX: 'left', alignY: 'bottom' },
            }}
          />
        </MeaningContainer>
        {item.usage && (
          <TextBlock
            text={replaceDashWithTilde(item.usage)}
            design={{ text: { size: 'small', color: color.GRAY_1, bold: false }, box: { alignX: 'left' } }}
          />
        )}
        {item.example?.[0]?.sentence && item.example[0]?.translation && (
          <Example
            sentence={item.example[0].sentence}
            translation={replaceDashWithTilde(item.example[0].translation)}
          />
        )}
        {item.kanji && item.kanji.length > 0 && (
          <>
            <Gap height={'2px'} />
            {item.kanji.map((k) => {
              const meaningText = replaceDashWithTilde(k.meaning?.[0] ?? '');
              const onyomiText = k.onyomi && k.onyomi.length > 0 ? `음독: ${k.onyomi.join(', ')}` : '';
              const kunyomiText = k.kunyomi && k.kunyomi.length > 0 ? `훈독: ${k.kunyomi.join(', ')}` : '';
              const description = [meaningText, onyomiText, kunyomiText].filter(Boolean).join(' / ');
              return (
                <KanjiYomiContainer key={uuid4()}>
                  <TextBlock
                    text={k.character}
                    design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
                  />
                  <TextBlock
                    text={description}
                    design={{ text: { size: 'small', color: color.GRAY_1, bold: false }, box: { alignX: 'left' } }}
                  />
                </KanjiYomiContainer>
              );
            })}
          </>
        )}
      </ContentContainer>
    </Canvas>
  );
};

const LABEL_DESIGN = {
  text: { size: 'large', color: color.BLACK_2, bold: true },
  box: { alignX: 'left', width: '100%' },
} as const;

const StudyNote: React.FC<IProps> = ({ vocabularies, grammars }) => {
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isJapanese = studyLanguage === EApiLanguageCode.JAPANESE;

  const Vocabularies = (vocabularies || []).map((vocabulary) => (
    <StudyNoteItem key={uuid4()} item={vocabulary} isJapanese={isJapanese} />
  ));

  const Grammars = (grammars || []).map((grammar) => (
    <StudyNoteItem key={uuid4()} item={grammar} isJapanese={isJapanese} />
  ));

  return (
    <Wrapper>
      {vocabularies && vocabularies.length > 0 && (
        <>
          <TextBlock text='어휘' design={LABEL_DESIGN} />
          <Gap height={'5px'} />
          <StudyItemsContainer>{Vocabularies}</StudyItemsContainer>
        </>
      )}
      {grammars && grammars.length > 0 && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock text='문법' design={LABEL_DESIGN} />
          <Gap height={'5px'} />
          <StudyItemsContainer>{Grammars}</StudyItemsContainer>
        </>
      )}
    </Wrapper>
  );
};

export default StudyNote;
